import { useMemo } from 'react'
import { useMatchScreenRedesign } from '../context/RedesignConfigContext'
import type { ModuleKey } from '../moduleRegistry'

/**
 * Reads a module's slice of `variables`, falling back to `defaults` when the
 * variable is absent or unparseable. Must be called inside
 * `RedesignConfigProvider`.
 *
 * `defaults` MUST be a stable reference (module-scope const) — an inline
 * literal causes the memo to recompute every render.
 *
 * @example
 * const DEFAULTS = { heading: 'We found your fiduciary matches.' } as const
 *
 * export const HeadingModule = () => {
 *   const { heading } = useModuleConfig(MODULE_KEYS.heading, DEFAULTS)
 *   return <h1>{heading}</h1>
 * }
 */
export function useModuleConfig<T extends Record<string, unknown>> (
  moduleKey: ModuleKey,
  defaults: T
): T {
  const { variables } = useMatchScreenRedesign()
  return useMemo(() => {
    const raw = variables[moduleKey]
    let parsed: unknown = raw
    if (typeof raw === 'string') {
      try { parsed = JSON.parse(raw) } catch { return defaults }
    }
    if (!parsed || typeof parsed !== 'object') return defaults
    return parsed as T
  }, [variables, moduleKey, defaults])
}
